/* eslint-disable @typescript-eslint/member-ordering */

import { ChangeDetectionStrategy, Component, computed, inject, input, InputSignal, model, ModelSignal, Signal } from '@angular/core';
import { ExpanderComponent } from '@libs/common/components';
import { IconComponent } from '@libs/components/icons';
import {
  ConfigurableBundleFormStateSlotModel,
  ConfigurableBundleSlotModel,
  ProductBaseModel,
  ProductBundleItemModel,
} from '@libs/shop/models';
import { ConfigurableBundleFormItemComponent } from '../configurable-bundle-form-item';
import { ConfigurableBundleFormUiService } from '../configurable-bundle-form-ui.service';

@Component({
  selector: 'ui-configurable-bundle-form-slot',
  imports: [
    ExpanderComponent,
    ConfigurableBundleFormItemComponent,
    IconComponent,

  ],
  templateUrl: './configurable-bundle-form-slot.component.html',
  styleUrl: './configurable-bundle-form-slot.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ConfigurableBundleFormSlotComponent {
  private readonly configurableBundleFormUiService: ConfigurableBundleFormUiService = inject(ConfigurableBundleFormUiService);

  public slot: InputSignal<ConfigurableBundleSlotModel> = input.required();
  public slotState: ModelSignal<ConfigurableBundleFormStateSlotModel> = model.required();
  public slotThresholdReachedIndex: InputSignal<number> = input.required();
  public disabled: InputSignal<boolean> = input(false);
  public cardsDisabled: Signal<boolean> = computed(() =>
    this.disabled() ||
    // we only disable the cards for the free slot when the limit is reached.
    // -> The customer can order more than the limit in the choice slots
    (this.slotState().thresholdStepReachedIndex === this.slotThresholdReachedIndex() && this.slot().slotType === 'free') ||
    this.slot().slotType === 'mandatory');

  public showAddToBundle: Signal<boolean> = computed(() => this.slot().slotType !== 'mandatory' && this.slot().products.length > 1);

  public description: Signal<string> = computed(() => {
    return this.configurableBundleFormUiService.getSlotDescriptionBySlotType(this.slot().slotType);
  });

  public currentThreshold: Signal<number> = computed(() => {
    return Number(this.slot().slotAmountSteps[this.slotThresholdReachedIndex()]);
  });

  public amountChanged(quantity: number | null, sku: string): void {
    if (!quantity) {
      return;
    }

    const itemState: ProductBundleItemModel | undefined = this.slotState().itemMap.get(sku);

    if (!itemState) {
      return;
    }

    const itemMap: Map<string, ProductBundleItemModel | undefined> = new Map(this.slotState().itemMap);

    itemMap.set(sku, { ...itemState, quantity });
    this.updateSlotState(itemMap);
  }

  public addToBundle(item: ProductBaseModel): void {
    const itemMap: Map<string, ProductBundleItemModel | undefined> = new Map(this.slotState().itemMap);

    itemMap.set(item.sku, item as ProductBundleItemModel);
    this.updateSlotState(itemMap);
  }

  public removeFromBundle(item: ProductBaseModel): void {
    const itemMap: Map<string, ProductBundleItemModel | undefined> = new Map(this.slotState().itemMap);

    itemMap.delete(item.sku);
    this.updateSlotState(itemMap);
  }

  private updateSlotState(itemMap: Map<string, ProductBundleItemModel | undefined>): void {
    const slotState: ConfigurableBundleFormStateSlotModel = {
      ...this.slotState(),
      itemMap,
    };

    const selectedAmount: number = this.calculateAmount(slotState);

    this.slotState.set({
      ...slotState,
      selectedAmount: selectedAmount,
      thresholdStepReachedIndex: this.configurableBundleFormUiService.findThresholdIndex(selectedAmount, this.slot().slotAmountSteps),
    });
  }

  private calculateAmount(slotState: ConfigurableBundleFormStateSlotModel): number {
    return slotState.itemMap.values().filter(x => !!x).reduce((a, b) => a + b.quantity, 0);
  }
}
